// "Draw to create" support: convert a [startTime, endTime] selection on the
// empty primary track into a new Event inserted into the active group.

import { frameGapToSeconds } from "./snapping.js";

const MIN_DRAW_DURATION_SEC = 0.05;

/**
 * Determine whether the timestamp range can be drawn as a new event without
 * overlapping any existing event in the group. Also computes the time-adjacent
 * previous and next events so callers can apply frame-gap clamping.
 *
 * @param {object[]} events
 * @param {number} startTime
 * @param {number} endTime
 * @returns {{ overlap: boolean, prev: object|null, next: object|null }}
 */
export function classifyDrawRange(events, startTime, endTime) {
    let prev = null;
    let next = null;
    for (let i = 0; i < events.length; i += 1) {
        const ev = events[i];
        if (!ev || ev.start === false || ev.end === false) continue;
        if (ev.end <= startTime) {
            if (!prev || ev.end > prev.end) prev = ev;
        } else if (ev.start >= endTime) {
            if (!next || ev.start < next.start) next = ev;
        } else {
            return { overlap: true, prev: null, next: null };
        }
    }
    return { overlap: false, prev, next };
}

/**
 * Clamp a drawn range to the gap defined by its neighbors, respecting a
 * minimum frame gap. Returns null if the resulting range would be shorter
 * than `MIN_DRAW_DURATION_SEC`.
 *
 * @param {object} input
 * @param {number} input.startTime
 * @param {number} input.endTime
 * @param {object|null} input.prev
 * @param {object|null} input.next
 * @param {number} input.frameGapSec
 * @returns {{ startTime: number, endTime: number }|null}
 */
export function clampDrawRange({ startTime, endTime, prev, next, frameGapSec }) {
    let s = Math.max(0, Math.min(startTime, endTime));
    let e = Math.max(startTime, endTime);
    if (prev && Number.isFinite(prev.end)) {
        s = Math.max(s, prev.end + frameGapSec);
    }
    if (next && Number.isFinite(next.start)) {
        e = Math.min(e, next.start - frameGapSec);
    }
    if (e - s < MIN_DRAW_DURATION_SEC) return null;
    return { startTime: s, endTime: e };
}

/**
 * Default frame gap helper kept here so all draw-related callers can compute
 * the same gap value from editor/project settings.
 *
 * @param {object} editorState
 * @param {object} projectState
 * @returns {number}
 */
export function drawFrameGapSec(editorState, projectState) {
    return frameGapToSeconds(
        editorState?.minFrameGap || 0,
        projectState?.frameRate || 29.97
    );
}

export { MIN_DRAW_DURATION_SEC };
